import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property, query } from 'lit/decorators.js';
import componentStyles from './se-capabilities-block.css.lit';
import { SeCapabilitiesList } from './se-capabilities-list';
import { SeCapabilitiesBlockItemType } from './se-capabilities-block';

/**
 * The SeCapabilitiesBlockItem component
 * @element se-capabilities-list-item
 */
@customElement('se-capabilities-list-item')
export class SeCapabilitiesBlockItem extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  private resizeObserver: ResizeObserver;

  /**
   * Ref to the parent element.
   */
  @property({ type: Object })
  parent: SeCapabilitiesList;

  /**
   * 'services' | 'industries'
   */
  @property({ type: String })
  type: SeCapabilitiesBlockItemType;

  /**
   * set by parent element.
   */
  @property({ type: String })
  id: string;

  @property({ type: String })
  url: string;

  @property({ type: String })
  text: string;

  /**
   * set by parent element if later than forth in line.
   */
  @property({ type: Boolean })
  toHide: boolean;

  /**
   * ref to a tag.
   */
  @query('.capabilities-block--link')
  link: HTMLAnchorElement;

  connectedCallback() {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });

    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver.unobserve(this);
  }

  render(): TemplateResult {
    return html`<a
      class="capabilities-block--link"
      id="${this.id}-link"
      href="${this.url}"
      >${this.text}</a
    >`;
  }

  /**
   * if the toHide attribute is present will cause the component to apply the hidden class to itself
   * after 30 ms, giving it enough time to report its initial height.
   */
  hideAfterDelay() {
    if (this.toHide) {
      setTimeout(() => this.link.classList.add('hidden'), 30);
    }
  }

  /**
   * Elements that aren't hidden update the parent component with their height on resize.
   * This can be caused by:
   *  1) A screen resize that compresses the component enough to cause wrapping.
   *  2) The element becoming unhidden, which again after a screen resize can mean a different height than initially reported.
   *  3) A resize of the component caused the cascading behavior of css.
   *    For example when a component first renders and reports its height not all styles have yet been applied.
   *    Font weights and family characteristics can cause components that are near the full width of the container to out grow them and
   *    require the text to wrap, which can double the height of the component AFTER it has reported its height to the parent.
   *    see screens in associated PR https://github.com/phase2/saul-ewing-design/pull/36/files
   */
  handleResize() {
    if (this.clientHeight > 0) {
      this.parent.heights[this.id] = this.getBoundingClientRect().height;
      this.parent.childInitiatedUpdate();
    }
  }

  firstUpdated() {
    // Report initial heigh to parent element
    this.parent.heights[this.id] = this.getBoundingClientRect().height;
    this.hideAfterDelay();
  }
}
